import CheckCircleFilled from "@ant-design/icons/CheckCircleFilled";
import ExclamationCircleOutlined from "@ant-design/icons/ExclamationCircleOutlined";
import IssuesCloseOutlined from "@ant-design/icons/IssuesCloseOutlined";
import React, { useContext, useMemo } from "react";
import { FollowSignalsContext } from "@shared/features/signals/common/FollowSignals.context";
import { ActionTooltip } from "common/Tooltips/ActionTooltip";
import { LanguageNameSpaces, Signal } from "@shared/types";
import { useTheme } from "styled-components";
import { FollowSignalType } from "@shared/features/signals/common/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";

interface FollowSignalAlertIconProps {
  signal: Signal;
}

export const SignalFollowAlertIcon = ({
  signal,
}: FollowSignalAlertIconProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const { openFollowModal } = useContext(FollowSignalsContext)!;
  const appTheme = useTheme();
  const isFollowingSignal = Boolean(
    signal.trade_exists?.length || signal.bot_trade_exists?.length
  );
  const requiresUpdate = Boolean(
    signal.requires_update?.length || signal.bot_requires_update?.length
  );
  const requiresCancel = Boolean(
    signal.requires_cancel?.length || signal.bot_requires_cancel?.length
  );

  const onTooltipClick = (followType: FollowSignalType) => {
    openFollowModal(signal, followType);
  };

  const getColor = () => {
    if (requiresCancel) return appTheme.colors.red;
    else if (requiresUpdate) return appTheme.colors.gold;
    else if (isFollowingSignal) return appTheme.colors.green;

    return "initial";
  };

  const getIconComponent = () => {
    if (requiresCancel) {
      return ExclamationCircleOutlined;
    } else if (requiresUpdate) {
      return IssuesCloseOutlined;
    } else if (isFollowingSignal) {
      return CheckCircleFilled;
    }
  };

  const getTooltipMessage = () => {
    let messageData: { title: string; body: string; onClick?: () => void } = {
      title: "",
      body: "",
    };
    if (signal.is_open) {
      if (requiresUpdate) {
        messageData.title = t("follow.messages.signal_updated");
        messageData.body = t("follow.actions.click_to_update");
        messageData.onClick = () => onTooltipClick(FollowSignalType.UPDATE);
      } else if (isFollowingSignal) {
        messageData.title = t("follow.messages.open_trade_exists");
      }
    } else {
      if (requiresCancel) {
        messageData.title = t("follow.messages.signal_canceled");
        messageData.body = t("follow.actions.click_to_close");
        messageData.onClick = () => onTooltipClick(FollowSignalType.CANCEL);
      }
    }
    return messageData;
  };

  const IconComponent = getIconComponent();

  const iconStyle = useMemo(() => {
    if (!IconComponent) return {};
    return {
      color: getColor(),
      fontSize: appTheme.typography.fontSizes.M,
      marginLeft: 8,
    };
  }, [IconComponent]);

  const { body, title, onClick } = getTooltipMessage();

  return (
    <>
      {IconComponent && (
        <ActionTooltip
          body={body}
          title={title}
          onClick={onClick}
          containerStyle={{ display: "contents" }}
        >
          <IconComponent style={iconStyle} />
        </ActionTooltip>
      )}
    </>
  );
};
